import { OnlineMatchesFeedback } from '@pflegia/types/common/enums/online-matches-feedback.enum';

import { Employer } from '../employer';
import { OnlineMatchStatus } from './online-match-status';

// Feedbacks used in the past, now only used in filters
export enum ObsoleteOnlineMatchesFeedback {
  FollowUpWithCem = 'follow_up_with_cem',
  Unresponsive = 'unresponsive',
}

export const OTHER_FEEDBACK = 'otherFeedback';

export const DEFAULT_FEEDBACK = 'DEFAULT';

export const PFLEGIA_RECRUITING_SIGNATURE = `{{{APP_NAME}}}s Recruiting Lösung`;

export const ONLINE_MATCHES_FEEDBACK_TRANSLATIONS = {
  [OnlineMatchesFeedback.AcceptedByAccident]: 'Versehentlich Match angenommen',
  [OnlineMatchesFeedback.CareerChangerCv]: 'QE/CV',
  [OnlineMatchesFeedback.DuplicateMatches]: 'Doppelt',
  [OnlineMatchesFeedback.EmployerNeedsOnlyFemaleApplicants]: 'Nur weibliche Bewerberinnen',
  [OnlineMatchesFeedback.EmployerNotLookingForAssistantNurses]: 'Keine HKs',
  [ObsoleteOnlineMatchesFeedback.FollowUpWithCem]: 'Final von CEM nachhaken',
  [OnlineMatchesFeedback.FollowUpWithCemUnresponsive]: 'Final von CEM nachhaken/Unresponsive',
  [OnlineMatchesFeedback.HaveBetterMatchAlready]: 'Liegt beim Träger vor',
  [OnlineMatchesFeedback.HaveOfflineMatchWithEmployer]: 'Liegt OFM vor, liegt Offline vor',
  [OnlineMatchesFeedback.LookingForApprenticeship]: 'Azubi, sucht Ausbildung',
  [OnlineMatchesFeedback.MarginalEmployment]: 'Geringfügig, GFB, Minijob',
  [OnlineMatchesFeedback.MatchAlreadySumbittedInternally]: 'Liegt intern vor',
  [OnlineMatchesFeedback.No43Or53]: 'Kein 43§, kein 53§',
  [OnlineMatchesFeedback.NoAssistantNurseVacany]: 'HK pausieren',
  [OnlineMatchesFeedback.NoBasicTrainingCertificate]: 'Kein Schein, Pflegebasiskurs, Basiskurs',
  [OnlineMatchesFeedback.NoDriversLicense]: 'Kein FS',
  [OnlineMatchesFeedback.NoHospitalExperience]: 'Keine Erfahrung im KH, keine Klinikerfahrung',
  [OnlineMatchesFeedback.NoLg1OrLg2]: 'Kein Lg1/Lg2, kein LG, kein Lg1, kein Lg2',
  [OnlineMatchesFeedback.NoMotherServices]: 'Keine Muttidienste möglich',
  [OnlineMatchesFeedback.NoNightShifts]: 'Keine DNW vakant',
  [OnlineMatchesFeedback.NoOneYearQualification]: 'Keine 1j, Ungelernt',
  [OnlineMatchesFeedback.NotEnoughExperience]: 'Keine BE in x Bereich',
  [OnlineMatchesFeedback.NoThreeYearQualification]: 'Keine 3j',
  [OnlineMatchesFeedback.NotPartnerAnymore]: 'Partnerschaft beendet',
  [OnlineMatchesFeedback.NoTrainingOrExperienceInCare]: 'Quereinsteiger, QE',
  [OnlineMatchesFeedback.NoTwoYearQualification]: 'Keine 2j',
  [OnlineMatchesFeedback.NoVacancy]: 'Keine Vakanz',
  [OnlineMatchesFeedback.NoVisa]: 'Kein Visa, Nicht EU, Ausland',
  [OnlineMatchesFeedback.ProfileDeleted]: 'Profil gelöscht',
  [OnlineMatchesFeedback.QualificationNotAcceptedInGermany]: 'Keine Anerkennung',
  [ObsoleteOnlineMatchesFeedback.Unresponsive]: 'Unresponsive',
  [OnlineMatchesFeedback.RejectionByKam]: 'Absage durch KAM',
  [OnlineMatchesFeedback.LanguageB1]: 'Sprachkenntnisse B1',
  [OnlineMatchesFeedback.WillingnessToTravel]: 'Entfernung/Reisebereitschaft',
  [OnlineMatchesFeedback.HoursDoNotFit]: 'SU passt nicht',
  [OnlineMatchesFeedback.NoApk]: 'Keine APK',
  [OnlineMatchesFeedback.OtherMissingQualification]: 'Andere AB/Quali fehlt',
  [OnlineMatchesFeedback.NoSchoolCertificate]: 'Kein Schulabschluss',
  [OTHER_FEEDBACK]: 'Sonstiges',
} as const;

export const FEEDBACK_SEND_TO_CEM = [
  OnlineMatchesFeedback.NoOneYearQualification,
  OnlineMatchesFeedback.NoTwoYearQualification,
  OnlineMatchesFeedback.NoThreeYearQualification,
  OnlineMatchesFeedback.NoLg1OrLg2,
  OnlineMatchesFeedback.No43Or53,
  OnlineMatchesFeedback.NoDriversLicense,
  OnlineMatchesFeedback.NoVacancy,
  OnlineMatchesFeedback.NoAssistantNurseVacany,
  OnlineMatchesFeedback.EmployerNeedsOnlyFemaleApplicants,
  OnlineMatchesFeedback.EmployerNotLookingForAssistantNurses,
  OnlineMatchesFeedback.NoBasicTrainingCertificate,
  OnlineMatchesFeedback.NotEnoughExperience,
  OnlineMatchesFeedback.NoHospitalExperience,
  OnlineMatchesFeedback.AcceptedByAccident,
  OnlineMatchesFeedback.NoNightShifts,
  OnlineMatchesFeedback.FollowUpWithCemUnresponsive,
  OnlineMatchesFeedback.CareerChangerCv,
  OnlineMatchesFeedback.RejectionByKam,
  OnlineMatchesFeedback.MatchAlreadySumbittedInternally,
  OnlineMatchesFeedback.HaveBetterMatchAlready,
  OnlineMatchesFeedback.NotPartnerAnymore,
  OnlineMatchesFeedback.WillingnessToTravel,
  OnlineMatchesFeedback.HoursDoNotFit,
  OnlineMatchesFeedback.NoApk,
  OnlineMatchesFeedback.OtherMissingQualification,
  ObsoleteOnlineMatchesFeedback.FollowUpWithCem,
  ObsoleteOnlineMatchesFeedback.Unresponsive,
];

export const FEEDBACK_DECLINE_ONLY = Object.values(OnlineMatchesFeedback).filter(feedback => !FEEDBACK_SEND_TO_CEM.includes(feedback));

export const FEEDBACK_TEMPLATES: { [key: string]: string } = {
  [DEFAULT_FEEDBACK]: `Hallo {{{NAME}}},

vielen Dank für Ihr Interesse an unserer Einrichtung und dass Sie sich die Zeit genommen haben, sich auf die ausgeschriebene Stelle zu bewerben.

Wir müssen Ihnen leider mitteilen, dass wir Ihnen eine Stelle bei uns im Hause gegenwärtig nicht anbieten können, da wir uns bereits für einen anderen Kandidaten entschieden haben.

Wir wünschen Ihnen weiterhin alles Gute und viel Erfolg für Ihre Zukunft.

Mit lieben Grüßen`,
  [OnlineMatchesFeedback.DuplicateMatches]: `Hallo {{{NAME}}},

Ihre Bewerbung liegt uns bereits in einem anderen Match vor. Sie erhalten dort zeitnah eine Rückmeldung zum aktuellen Stand.

Bis dahin!

Liebe Grüße`,
  [OnlineMatchesFeedback.NoOneYearQualification]: `Hallo {{{NAME}}},

vielen Dank für Ihr Interesse an unserer Einrichtung!

Leider ist die abgeschlossene 1-jährige Ausbildung zum Pflegehelfer bei uns eine Einstellungsvoraussetzung, weshalb wir Ihnen hiermit leider absagen müssen. Falls Sie in naher Zukunft eine Ausbildung zum Pflegehelfer absolvieren oder diese tatsächlich besitzen sollten, freuen wir uns über eine kurze Nachricht. Mit einer Ausbildung können wir Sie wieder in unserem Bewerbungsprozess aufnehmen.

Wir wünschen Ihnen alles Gute für die Zukunft!

Viele Grüße`,
  [OnlineMatchesFeedback.NoTwoYearQualification]: `Hallo {{{NAME}}},

vielen Dank für Ihr Interesse an unserer Einrichtung!

Leider ist die abgeschlossene 2-jährige Ausbildung zum Pflegehelfer bei uns eine Einstellungsvoraussetzung, weshalb wir Ihnen hiermit leider absagen müssen. Falls Sie in naher Zukunft eine 2-jährige Ausbildung zum Pflegehelfer absolvieren oder diese tatsächlich besitzen sollten, freuen wir uns über eine kurze Nachricht. Mit einer Ausbildung können wir Sie wieder in unserem Bewerbungsprozess aufnehmen.

Wir wünschen Ihnen alles Gute für die Zukunft!

Viele Grüße`,
  [OnlineMatchesFeedback.NoThreeYearQualification]: `Hallo {{{NAME}}},

vielen Dank für Ihr Interesse an unserer Einrichtung!

Leider ist die abgeschlossene 3-jährige Ausbildung zur Fachkraft bei uns eine Einstellungsvoraussetzung, weshalb wir Ihnen hiermit leider absagen müssen. Falls Sie in naher Zukunft eine 3-jährige Ausbildung zur Fachkraft in der Pflege absolvieren oder diese tatsächlich besitzen sollten, freuen wir uns über eine kurze Nachricht. Mit solch einer Ausbildung können wir Sie wieder in unserem Bewerbungsprozess aufnehmen.

Wir wünschen Ihnen alles Gute für die Zukunft!

Viele Grüße`,
  [OnlineMatchesFeedback.NoLg1OrLg2]: `Hallo {{{NAME}}},

vielen Dank für Ihr Interesse an unserem Unternehmen.

Nach interner Rücksprache reichen Ihre Qualifikationen leider nicht für die Stelle aus, da wir zumindest die Weiterbildung zur Behandlungspflege LG1/LG2 voraussetzen müssen. Daher müssen wir Ihnen hiermit leider absagen.

Wenn Sie die Weiterbildung in Zukunft absolvieren oder tatsächlich besitzen sollten oder diese in Ihre Ausbildung inbegriffen ist, würden wir uns über eine Rückmeldung freuen.

Wir wünschen Ihnen weiterhin alles Gute und viel Erfolg für die Zukunft.

Liebe Grüße`,
  [OnlineMatchesFeedback.No43Or53]: `Hallo {{{NAME}}},

Danke für Ihr Interesse!

Leider kommen wir dieses Mal jedoch nicht zusammen. Für diese Stelle müssen wir eine Qualifikation zur Betreuungskraft (z.B. nach 43b, 53b SGB XI o.Ä.) voraussetzen.

Wenn Sie diese doch besitzen oder in naher Zukunft absolvieren, dann melden Sie sich sehr gerne hier im Chat zurück.

Andernfalls müssen wir Ihnen hiermit leider absagen und wünschen Ihnen alles Gute für Ihre berufliche und persönliche Zukunft!

Liebe Grüße`,
  [OnlineMatchesFeedback.NoDriversLicense]: `Hallo {{{NAME}}},

vielen Dank für Ihr Interesse an unserer Einrichtung! Leider ist ein gültiger Führerschein bei uns eine Einstellungsvoraussetzung, weshalb wir Ihnen hiermit leider absagen müssen. Falls Sie in naher Zukunft einen Führerschein erwerben werden oder tatsächlich einen besitzen, würden wir uns sehr freuen, wieder von Ihnen zu hören. In diesem Fall würden wir Sie wieder in unseren Bewerbungsprozess aufnehmen. Wir wünschen Ihnen alles Gute für die Zukunft!

Viele Grüße`,
  [OnlineMatchesFeedback.NoVacancy]: `Hallo {{{NAME}}},

vielen Dank für Ihr Interesse an unserem Unternehmen.

Wir müssen Ihnen leider mitteilen, dass die Stelle kurzfristig besetzt werden konnte und demnach nicht mehr vakant ist.

Gerne kommen wir auf Sie zurück, wenn wir neue vakante Stellen haben.

Wir wünschen Ihnen weiterhin alles Gute und viel Erfolg für Ihre Zukunft.

Mit lieben Grüßen`,
  [OnlineMatchesFeedback.NoAssistantNurseVacany]: `Hallo {{{NAME}}},

vielen Dank für Ihr Interesse an unserer Einrichtung.

Wir müssen Ihnen jedoch leider mitteilen, dass die Stelle kurzfristig besetzt werden konnte und alle Positionen für Hilfskräfte daher zurzeit besetzt sind. Gerne kommen wir auf Sie zurück, wenn wir neue vakante Stellen haben. Wir wünschen Ihnen alles Gute und viel Erfolg für Ihre Zukunft!

Mit lieben Grüßen`,
  [OnlineMatchesFeedback.EmployerNeedsOnlyFemaleApplicants]: `Hallo {{{NAME}}},

vielen Dank für Ihr Interesse an unserem Unternehmen. Wir müssen Ihnen jedoch leider mitteilen, dass die Stelle kurzfristig besetzt werden konnte und demnach nicht mehr vakant ist. Gerne kommen wir auf Sie zurück, wenn wir neue vakante Stellen haben. Wir wünschen Ihnen weiterhin alles Gute und viel Erfolg für Ihre Zukunft!

Liebe Grüße`,
  [OnlineMatchesFeedback.EmployerNotLookingForAssistantNurses]: `Hallo {{{NAME}}},

vielen Dank für Ihr Interesse an unserem Unternehmen.

Wir müssen Ihnen leider mitteilen, dass die Stelle kurzfristig besetzt werden konnte und demnach nicht mehr vakant ist.

Gerne kommen wir auf Sie zurück, wenn wir neue vakante Stellen haben.

Wir wünschen Ihnen weiterhin alles Gute und viel Erfolg für Ihre Zukunft.

Mit lieben Grüßen`,
  [OnlineMatchesFeedback.NotPartnerAnymore]: `Hallo {{{NAME}}},

vielen Dank für Ihr Interesse an unserem Unternehmen.

Wir müssen Ihnen leider mitteilen, dass die Stelle kurzfristig besetzt werden konnte und demnach nicht mehr vakant ist.

Gerne kommen wir auf Sie zurück, wenn wir neue vakante Stellen haben.

Wir wünschen Ihnen weiterhin alles Gute und viel Erfolg für Ihre Zukunft.

Mit lieben Grüßen`,
  [OnlineMatchesFeedback.FollowUpWithCemUnresponsive]: `Hallo {{{NAME}}},

wir haben leider in den letzten Tagen nichts weiter von Ihnen gehört. Besteht von Ihrer Seite aus noch Interesse?

Wenn dem so ist, senden Sie uns bitte noch die angeforderten Unterlagen bzw. Informationen.

Bis dahin würden wir den Kontakt hier zunächst pausieren.

Wir wünschen Ihnen alles Gute und verbleiben mit besten Grüßen`,
  [OnlineMatchesFeedback.NotEnoughExperience]: `Hallo {{{NAME}}},

vielen Dank für Ihr Interesse! Leider können wir Ihre Bewerbung nicht berücksichtigen, da Sie nicht genügend Berufserfahrung im benötigten Fachbereich vorweisen können. Wir wünschen Ihnen alles Gute und viel Erfolg für die Zukunft!

Liebe Grüße`,
  [OnlineMatchesFeedback.NoHospitalExperience]: `Hallo {{{NAME}}},

vielen Dank für Ihr Interesse an unserem Unternehmen. Nach interner Rücksprache reichen Ihre Qualifikationen leider nicht für die Stelle aus, weil Sie zum jetzigen Zeitpunkt nicht ausreichend Klinikerfahrungen mitbringen. Wir wünschen Ihnen weiterhin alles Gute und viel Erfolg für die Zukunft.

Mit lieben Grüßen`,
  [OnlineMatchesFeedback.NoTrainingOrExperienceInCare]: `Hallo {{{NAME}}},

vielen Dank für Ihr Interesse an unserer Einrichtung und dass Sie sich die Zeit genommen haben, sich auf die ausgeschrieben Stelle zu bewerben. Wir müssen Ihnen leider mitteilen, dass wir Ihnen eine Stelle bei uns im Hause gegenwärtig nicht anbieten können, da wir uns bereits für einen anderen Kandidaten entschieden haben. Wir wünschen Ihnen weiterhin alles Gute und viel Erfolg für Ihre Zukunft.

Mit lieben Grüßen`,
  [OnlineMatchesFeedback.HaveBetterMatchAlready]: `Hallo {{{NAME}}},

da Sie sich bereits bei einer Einrichtung von unserem Träger beworben haben, liegen uns Ihre Unterlagen schon intern vor. Wir geben Ihnen dort zeitnah Bescheid, wie der weitere Ablauf ist.

Bis dahin!

Liebe Grüße`,
  [OnlineMatchesFeedback.AcceptedByAccident]: `Hallo {{{NAME}}},

wenn sich bei Ihnen etwas ändert und Sie wieder Interesse haben sollten, können Sie sich gerne jederzeit hier im Chat zurückmelden!

Wir wünschen Ihnen alles Gute!

Liebe Grüße`,
  [OnlineMatchesFeedback.HaveOfflineMatchWithEmployer]: `Hallo {{{NAME}}},

vielen Dank für Ihr Interesse an unserer Einrichtung!

Wir stehen bereits mit Ihrem Karrierecoach von Pflegia in Verbindung und werden über diesen Weg weiterhin mit Ihnen kommunizieren und Ihre Fragen beantworten.

Wir freuen uns!

Liebe Grüße`,

  [OnlineMatchesFeedback.NoNightShifts]: `Hallo {{{NAME}}},

vielen Dank für Ihr Interesse an unserem Unternehmen und Ihre Geduld.

Nach internen Rücksprachen können wir leider derzeit keine Dauernachtwachen bei uns einstellen. Wären Sie auch an einer Stelle im Tagdienst interessiert?

Andernfalls müssen wir Ihnen hiermit absagen und wünschen Ihnen alles Gute für die Zukunft!

Viele Grüße`,

  [OnlineMatchesFeedback.NoBasicTrainingCertificate]: `Hallo {{{NAME}}},

Danke für Ihr Interesse! Leider kommen wir dieses Mal jedoch nicht zusammen. Für eine Stelle als Helfer in unserem Hause müssen wir leider mindestens einen Kurs oder eine Weiterbildung voraussetzen. Wenn Sie in Zukunft eine absolvieren oder tatsächlich besitzen sollten, dann sind Sie herzlich dazu eingeladen, uns hier im Chat noch einmal zu schreiben. Wir wünschen Ihnen alles Gute für Ihre berufliche und persönliche Zukunft!

Mit lieben Grüßen`,

  [OnlineMatchesFeedback.CareerChangerCv]: `Hallo {{{NAME}}},
vielen Dank für Ihre Bewerbung!

Sie haben in Ihrem Profilangaben angegeben, dass sie als Quereinsteiger bei uns im Hause anfangen möchten. Haben Sie bereits Erfahrungen in der Pflege sammeln können?

Falls ja, dann schicken Sie uns gerne Ihren aktuellen Lebenslauf zu!

Wir freuen uns auf Ihre Rückmeldung!

Liebe Grüße`,
  [OnlineMatchesFeedback.QualificationNotAcceptedInGermany]: `Hallo {{{NAME}}},

vielen Dank für Ihr Interesse an unserer Einrichtung!

Leider ist die abgeschlossene Anerkennung bei uns eine Einstellungsvoraussetzung.

Falls Sie in naher Zukunft die Anerkennung abschließen oder diese tatsächlich besitzen sollten, freuen wir uns über eine kurze Nachricht.

Mit einer Anerkennung können wir Sie wieder in unserem Bewerbungsprozess aufnehmen.

Wir wünschen Ihnen alles Gute für die Zukunft!

Viele Grüße
`,
  [OnlineMatchesFeedback.MarginalEmployment]: `Hallo {{{NAME}}},

danke für Ihr Interesse. Kommt für Sie auch eine Stelle in Voll oder Teilzeit in Frage? Dann melden Sie sich gerne mit Ihrem möglichen Stundenumfang direkt hier im Chat zurück!

Wenn Sie nur einen Minijob suchen, dann müssen wir Ihnen hiermit leider absagen und wünschen Ihnen alles Gute und viel Erfolg für die Zukunft!

Liebe Grüße`,

  [OnlineMatchStatus.TooManyMatches]: `Hallo {{{NAME}}},

vielen Dank für Ihre Bewerbung und Ihr Interesse an unserer Einrichtung!

Da für unsere ausgeschriebene Stelle zum jetzigen Zeitpunkt sehr viele Bewerbungen eingegangen sind, wird die Bewerberauswahl noch einige Zeit in Anspruch nehmen. Hierfür bitten wir Sie um Verständnis.

Wir melden uns zu einem späteren Zeitpunkt bei Ihnen hier im Chat zurück.

Freundliche Grüße`,
  [OnlineMatchesFeedback.NoVisa]: `Hallo,

leider ist es uns nicht möglich, Bewerber aus dem EU-Ausland einzustellen, weshalb wir Ihnen hiermit leider absagen müssen.

Der Aufwand, eine Pflegekraft für uns nach Deutschland zu holen, ist einfach zu groß.

Es gibt aber eine Webseite, die sich darauf spezialisiert hat, Leuten aus verschiedenen Ländern einen Job zu vermitteln: https://workeer.de/.

Außerdem finden sich hier einige Informationen, wie Sie ein Visum bekommen können: https://www.make-it-in-germany.com/de/visum/arten/arbeitsplatzsuche.

Wir wünschen Ihnen nur das Beste und hoffen, dass Sie einen Job in Deutschland finden!

Mit lieben Grüßen`,
  [OnlineMatchesFeedback.NoMotherServices]: `Hallo {{{NAME}}},

Danke für Ihr Interesse an unserer Einrichtung!

Wir haben zunächst eine kleine Nachfrage:

Suchen Sie ausschließlich nach einer Stelle im Elterndienst oder sind auch andere Schichten für Sie möglich?

Wir freuen uns auf Ihre Rückmeldung.

Liebe Grüße`,
  [OnlineMatchesFeedback.RejectionByKam]: `Hallo {{{NAME}}},

vielen Dank für Ihr Interesse an unserem Unternehmen.

Wir müssen Ihnen leider mitteilen, dass die Stelle kurzfristig besetzt werden konnte und demnach nicht mehr vakant ist.Wir wünschen Ihnen weiterhin alles Gute und viel Erfolg für Ihre Zukunft.

Mit lieben Grüßen`,
  [OnlineMatchesFeedback.LanguageB1]: `Hallo {{{NAME}}},

Danke für Ihr Interesse! Für die Einstellung in unserer Einrichtung müssen wir leider mindestens ein deutsches Sprachniveau von B1 voraussetzen, falls dies bei Ihnen nicht der Fall sein sollte, müssen wir Ihnen leider absagen.

Sollten Sie in naher Zukunft das Sprachniveau von B1 erwerben, würden wir uns sehr freuen, wieder von Ihnen zu hören.

Wir wünschen Ihnen alles Gute für Ihre berufliche und persönliche Zukunft!

Mit lieben Grüßen`,
  [OnlineMatchesFeedback.WillingnessToTravel]: `Hallo {{{NAME}}},

da die Einsatzorte für diese Stelle nicht ohne Weiteres zu erreichen sind, müssen wir Ihnen leider absagen.

Sollten Sie jedoch über weitreichende Reisebereitschaft verfügen und mobil sein, melden Sie sich gern direkt hier im Chat zurück.

Wir wünschen Ihnen alles Gute für die Zukunft!

Mit freundlichen Grüßen`,
  [OnlineMatchesFeedback.HoursDoNotFit]: `Hallo {{{NAME}}},

danke für Ihr Interesse!
Leider können wir Ihnen nicht den von Ihnen gewünschten Stellenumfang anbieten.

Wenn sich in Zukunft Ihre Situation ändern sollte, schreiben Sie uns doch gerne hier im Chat eine Nachricht.

Wir wünschen Ihnen alles Gute und verbleiben mit besten Grüßen`,
  [OnlineMatchesFeedback.NoApk]: `Hallo {{{NAME}}},

vielen Dank für Ihr Interesse an unserer Einrichtung!

Leider müssen wir Ihnen mitteilen, dass wir derzeit ausschließlich Gesundheits-und Krankenpfleger:innen einstellen.

Als Altenpflegefachkraft können Sie eine Weiterbildung zum/r Gesundheits- und Krankenpfleger:in absolvieren. Falls Sie diese Weiterbildung bereits absolviert haben oder derzeit in der Weiterbildung sind, melden Sie sich gerne erneut mit der Info hier im Chat.

Wir wünschen Ihnen für Ihre berufliche Zukunft alles Gute.

Mit lieben Grüßen`,
  [OnlineMatchesFeedback.OtherMissingQualification]: `Hallo {{{NAME}}},

danke für Ihr Interesse!

Leider bringen Sie für die Stelle nicht die erforderliche Qualifikation mit.

Falls Sie diese in naher Zukunft absolvieren oder tatsächlich besitzen, freuen wir uns über eine kurze Nachricht hier im Chat.

Wir wünschen Ihnen alles Gute und verbleiben mit besten Grüßen`,
  [OnlineMatchesFeedback.NoSchoolCertificate]: `Hallo {{{NAME}}},

vielen Dank für Ihr Interesse an unserer Einrichtung!

Leider können wir Ihnen aktuell jedoch keinen Ausbildungsplatz in unserer Einrichtung anbieten, da wir dafür einen in Deutschland anerkannten Realschulabschluss voraussetzen müssen. Sollten Sie diesen doch besitzen oder bald erhalten, dann melden Sie sich sehr gerne mit dem entsprechenden Nachweis hier im Chat zurück.

Andernfalls müssen wir Ihnen hiermit leider absagen und wünschen Ihnen alles Gute und viel Erfolg für die Zukunft!

Mit lieben Grüßen`,
};

const getSignature = (employer?: Employer) => {
  if (employer?.signature && !employer.showPflegiaRecruitingSignature) return employer?.signature;
  return PFLEGIA_RECRUITING_SIGNATURE;
};

const getTemplateWithSignature = (feedback: string = DEFAULT_FEEDBACK, employer?: Employer) => {
  const template = FEEDBACK_TEMPLATES[feedback] ?? FEEDBACK_TEMPLATES[DEFAULT_FEEDBACK];
  const signature = getSignature(employer);
  if (!signature) return template;

  return `${template}
${signature}`;
};

export const getFeedbackTemplate = (allFeedback?: string[] | null, employer?: Employer) => {
  const [feedback] = allFeedback ?? [];
  return getTemplateWithSignature(feedback, employer);
};
